import React, { useState, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
//import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import useWindowSize from "../hooks/useWindowSize";

pdfjs.GlobalWorkerOptions.workerSrc =
    process.env.PUBLIC_URL + "/pdf.worker.mjs";

const PDF_PATH =
    process.env.PUBLIC_URL + "/docs/Benutzerhandbuch_FLIK_Suche.pdf";

const TOOLBAR_HEIGHT = 50;
const MAX_PAGE_WIDTH = 1000;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.0;
const ZOOM_STEP = 0.25;
const DIALOG_HORIZONTAL_PADDING = 48;

export default function UserManual() {
    const [numPages, setNumPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageHeight, setPageHeight] = useState(null);
    const [zoom, setZoom] = useState(0.75);
    const { width: windowWidth } = useWindowSize();

    const basePageWidth = Math.min(
        MAX_PAGE_WIDTH,
        (windowWidth ?? MAX_PAGE_WIDTH) - DIALOG_HORIZONTAL_PADDING,
    );
    const pageWidth = basePageWidth * zoom;

    const onDocumentLoadSuccess = ({ numPages: total }) => {
        setNumPages(total);
        setCurrentPage(1);
    };

    const onPageLoadSuccess = useCallback(
        (page) => {
            const { width, height } = page.originalWidth
                ? { width: page.originalWidth, height: page.originalHeight }
                : page;
            const scale = (basePageWidth / width) * zoom;
            setPageHeight(height * scale);
        },
        [basePageWidth, zoom],
    );

    const goToPreviousPage = () =>
        setCurrentPage((prev) => Math.max(prev - 1, 1));

    const goToNextPage = () =>
        setCurrentPage((prev) => Math.min(prev + 1, numPages));

    const zoomIn = () =>
        setZoom((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
    const zoomOut = () =>
        setZoom((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM));

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "75vh",
                width: "100%",              

            }}
        >
            <Box
                sx={{
                    flex: 1,
                    overflow: "auto",
                    display: "flex",
                    justifyContent: "center",
                    py: 2,
                    backgroundColor: "#f5f5f5",
                }}
            >
                <Document
                    file={PDF_PATH}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                        <Typography variant="body2" sx={{ py: 4 }}>
                            PDF wird geladen…
                        </Typography>
                    }
                    error={
                        <Typography
                            variant="body2"
                            color="error"
                            sx={{ py: 4 }}
                        >
                            Fehler beim Laden des Dokuments.
                        </Typography>
                    }
                >
                    <div
                        style={{
                            height: pageHeight ?? "auto",
                            width: pageWidth,
                            overflow: "hidden",
                        }}
                    >
                        <Page
                            pageNumber={currentPage}
                            width={pageWidth}
                            renderTextLayer={false}
                            renderAnnotationLayer={true}
                            onLoadSuccess={onPageLoadSuccess}
                            loading=""
                        />
                    </div>
                </Document>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 0.5,
                    minHeight: TOOLBAR_HEIGHT,
                    px: 1,
                    py: 0.5,
                    borderTop: "1px solid #e0e0e0",
                    backgroundColor: "#fafafa",
                    borderRadius: "0 0 4px 4px",
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={goToPreviousPage}
                        disabled={currentPage <= 1}
                        sx={{ minWidth: 32, px: 1 }}
                    >
                        &lsaquo;
                    </Button>
                    <Typography variant="body2" noWrap>
                        Seite {currentPage} / {numPages ?? "…"}
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={goToNextPage}
                        disabled={currentPage >= numPages}
                        sx={{ minWidth: 32, px: 1 }}
                    >
                        &rsaquo;
                    </Button>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={zoomOut}
                        disabled={zoom <= MIN_ZOOM}
                        sx={{ minWidth: 32, px: 1 }}
                        title="Verkleinern"
                    >
                        −
                    </Button>
                    <Typography
                        variant="body2"
                        noWrap
                        sx={{ minWidth: 50, textAlign: "center" }}
                    >
                        {Math.round(zoom * 100)}%
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={zoomIn}
                        disabled={zoom >= MAX_ZOOM}
                        sx={{ minWidth: 32, px: 1 }}
                        title="Vergrößern"
                    >
                        +
                    </Button>

                    <Button
                        size="small"
                        variant="contained"
                        component="a"
                        href={PDF_PATH}
                        download="Benutzerhandbuch_FLIK_Suche.pdf"
                        sx={{
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            ml: 1,
                        }}
                    >
                        Herunterladen
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
